import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useTheme } from '../contexts/ThemeContext';
import { useChat } from '../contexts/ChatContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Home, Calendar, Inbox, Settings, LogOut, Zap, Menu, X, Shield, 
  Plus, Mic, LayoutDashboard, CheckSquare, Timer, FolderKanban, 
  MessageSquare, FileText, Moon, Sun, ClipboardList, Rocket
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from './ui/dropdown-menu';
import VoiceInput from './VoiceInput';
import UnifiedTaskModal from './UnifiedTaskModal';
import NotificationBell from './NotificationBell';
import api, { launchpadApi } from '../lib/api';
import { toast } from 'sonner';

// Import extracted components
import { WorkspacesList, LaunchpadItem, MobileLaunchpad } from './sidebar';
import {
  CreateFolderDialog,
  EditFolderDialog,
  MoveToFolderDialog,
  InviteUserDialog,
  CreateProjectDialog,
  ProjectSettingsDialog,
  WorkspaceSettingsDialog,
  CreateLaunchpadFolderDialog,
  EditLaunchpadFolderDialog,
  ShareLaunchpadFolderDialog,
  LaunchpadLinkDialog,
  ConfirmDeleteLaunchpadDialog
} from './dialogs';

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { workspaces, folders, unfiledWorkspaces, currentWorkspace, switchWorkspace, refreshWorkspaces, createFolder, updateFolder, deleteFolder, addWorkspaceToFolder, removeWorkspaceFromFolder } = useWorkspace();
  const { isDark, toggleTheme } = useTheme();
  const { unreadCount, workspaceUnread, projectUnread } = useChat();
  const navigate = useNavigate();
  const location = useLocation();
  
  // UI State
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('sidebar_width');
    return saved ? parseInt(saved) : 256;
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Expandable folders and workspaces state - persisted in localStorage
  const [expandedFolders, setExpandedFolders] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_expanded_folders');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [expandedWorkspaces, setExpandedWorkspaces] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_expanded_workspaces');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [workspaceProjects, setWorkspaceProjects] = useState({});
  const [loadingProjects, setLoadingProjects] = useState({});

  // Sidebar resize handlers
  const SIDEBAR_MIN = 220;
  const SIDEBAR_MAX = 480;
  const SIDEBAR_DEFAULT = 256;

  const startResize = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const onMouseMove = (e) => {
      const newWidth = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, startWidth + (e.clientX - startX)));
      setSidebarWidth(newWidth);
    };
    const onMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setSidebarWidth((w) => { localStorage.setItem('sidebar_width', String(w)); return w; });
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [sidebarWidth]);

  const resetSidebarWidth = useCallback(() => {
    setSidebarWidth(SIDEBAR_DEFAULT);
    localStorage.setItem('sidebar_width', String(SIDEBAR_DEFAULT));
  }, []);

  
  // Folder dialog state
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#6B7280');
  const [creatingFolder, setCreatingFolder] = useState(false);
  
  const [editFolderOpen, setEditFolderOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [editFolderColor, setEditFolderColor] = useState('#6B7280');
  const [savingFolder, setSavingFolder] = useState(false);
  
  // Move workspace dialog state
  const [moveToFolderOpen, setMoveToFolderOpen] = useState(false);
  const [movingWorkspace, setMovingWorkspace] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [movingToFolder, setMovingToFolder] = useState(false);
  
  // Invite dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteWorkspace, setInviteWorkspace] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteLoading, setInviteLoading] = useState(false);
  
  // Project dialog state
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [projectWorkspace, setProjectWorkspace] = useState(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectColor, setNewProjectColor] = useState('#4A9CC7');
  const [newProjectIcon, setNewProjectIcon] = useState('folder');
  const [creatingProject, setCreatingProject] = useState(false);
  
  const [projectSettingsOpen, setProjectSettingsOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectColor, setEditProjectColor] = useState('#4A9CC7');
  const [editProjectIcon, setEditProjectIcon] = useState('folder');
  const [savingProject, setSavingProject] = useState(false);
  
  // Workspace settings state
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settingsWorkspace, setSettingsWorkspace] = useState(null);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Launchpad state
  const [createLaunchpadFolderOpen, setCreateLaunchpadFolderOpen] = useState(false);
  const [editLaunchpadFolderOpen, setEditLaunchpadFolderOpen] = useState(false);
  const [shareLaunchpadFolderOpen, setShareLaunchpadFolderOpen] = useState(false);
  const [launchpadLinkOpen, setLaunchpadLinkOpen] = useState(false);
  const [confirmDeleteLaunchpadOpen, setConfirmDeleteLaunchpadOpen] = useState(false);
  const [selectedLaunchpadFolder, setSelectedLaunchpadFolder] = useState(null);
  const [selectedLaunchpadLink, setSelectedLaunchpadLink] = useState(null);
  const [launchpadLoading, setLaunchpadLoading] = useState(false);
  const launchpadRef = useRef(null);
  const mobileLaunchpadRef = useRef(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Get active project ID from URL
  const getActiveProjectId = () => {
    const params = new URLSearchParams(location.search);
    return params.get('project');
  };
  const activeProjectId = getActiveProjectId();

  // Fetch projects for a workspace
  const fetchWorkspaceProjects = useCallback(async (workspaceId) => {
    if (workspaceProjects[workspaceId] || loadingProjects[workspaceId]) return;
    
    setLoadingProjects(prev => ({ ...prev, [workspaceId]: true }));
    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`);
      setWorkspaceProjects(prev => ({ ...prev, [workspaceId]: response.data.projects || [] }));
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setWorkspaceProjects(prev => ({ ...prev, [workspaceId]: [] }));
    } finally {
      setLoadingProjects(prev => ({ ...prev, [workspaceId]: false }));
    }
  }, [workspaceProjects, loadingProjects]);

  // Fetch projects for persisted expanded workspaces on mount
  useEffect(() => {
    const expandedIds = Object.entries(expandedWorkspaces)
      .filter(([_, isExpanded]) => isExpanded)
      .map(([id]) => id);
    
    expandedIds.forEach(workspaceId => {
      if (!workspaceProjects[workspaceId] && !loadingProjects[workspaceId]) {
        fetchWorkspaceProjects(workspaceId);
      }
    });
  }, [expandedWorkspaces, workspaceProjects, loadingProjects, fetchWorkspaceProjects]);

  // Toggle handlers
  const toggleWorkspace = (workspaceId) => {
    const isExpanding = !expandedWorkspaces[workspaceId];
    const newState = { ...expandedWorkspaces, [workspaceId]: isExpanding };
    setExpandedWorkspaces(newState);
    // Persist to localStorage
    try {
      localStorage.setItem('sidebar_expanded_workspaces', JSON.stringify(newState));
    } catch (e) {
      // Silent fail for localStorage
    }
    if (isExpanding) {
      fetchWorkspaceProjects(workspaceId);
    }
  };

  const toggleFolder = (folderId) => {
    const newState = { ...expandedFolders, [folderId]: !expandedFolders[folderId] };
    setExpandedFolders(newState);
    // Persist to localStorage
    try {
      localStorage.setItem('sidebar_expanded_folders', JSON.stringify(newState));
    } catch (e) {
      // Silent fail for localStorage
    }
  };

  // Folder handlers
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      await createFolder({ name: newFolderName.trim(), color: newFolderColor });
      toast.success('Folder created!');
      setCreateFolderOpen(false);
      setNewFolderName('');
      setNewFolderColor('#6B7280');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const openEditFolderDialog = (folder, e) => {
    e?.stopPropagation();
    setEditingFolder(folder);
    setEditFolderName(folder.name);
    setEditFolderColor(folder.color || '#6B7280');
    setEditFolderOpen(true);
  };

  const handleSaveFolder = async () => {
    if (!editingFolder || !editFolderName.trim()) return;
    setSavingFolder(true);
    try {
      await updateFolder(editingFolder.folder_id, { name: editFolderName.trim(), color: editFolderColor });
      toast.success('Folder updated!');
      setEditFolderOpen(false);
      setEditingFolder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update folder');
    } finally {
      setSavingFolder(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!editingFolder) return;
    if (!window.confirm(`Delete "${editingFolder.name}"? Workspaces inside will be moved to root.`)) return;
    try {
      await deleteFolder(editingFolder.folder_id);
      toast.success('Folder deleted');
      setEditFolderOpen(false);
      setEditingFolder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete folder');
    }
  };

  // Move to folder handlers
  const openMoveToFolderDialog = (workspace, e) => {
    e?.stopPropagation();
    setMovingWorkspace(workspace);
    setSelectedFolderId(workspace.folder_id || '');
    setMoveToFolderOpen(true);
  };

  const handleMoveToFolder = async () => {
    if (!movingWorkspace) return;
    setMovingToFolder(true);
    try {
      const currentFolderId = movingWorkspace.folder_id;
      if (!selectedFolderId || selectedFolderId === 'none') {
        if (currentFolderId) {
          await removeWorkspaceFromFolder(currentFolderId, movingWorkspace.workspace_id);
          toast.success('Workspace moved to root');
        }
      } else {
        if (currentFolderId && currentFolderId !== selectedFolderId) {
          await api.delete(`/folders/${currentFolderId}/workspaces/${movingWorkspace.workspace_id}`);
        }
        if (currentFolderId !== selectedFolderId) {
          await addWorkspaceToFolder(selectedFolderId, movingWorkspace.workspace_id);
          const targetFolder = folders.find(f => f.folder_id === selectedFolderId);
          toast.success(`Workspace moved to "${targetFolder?.name || 'folder'}"`);
        }
      }
      setMoveToFolderOpen(false);
      setMovingWorkspace(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move workspace');
    } finally {
      setMovingToFolder(false);
    }
  };

  // Invite handlers
  const openInviteDialog = (workspace, e) => {
    e?.stopPropagation();
    setInviteWorkspace(workspace);
    setInviteDialogOpen(true);
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim() || !inviteWorkspace) return;
    setInviteLoading(true);
    try {
      await api.post(`/workspaces/${inviteWorkspace.workspace_id}/invite`, {
        email: inviteEmail.trim(),
        role: inviteRole
      });
      toast.success(`Invited ${inviteEmail} to ${inviteWorkspace.name}`);
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      setInviteWorkspace(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setInviteLoading(false);
    }
  };

  // Project handlers
  const openCreateProjectDialog = (workspace, e) => {
    e?.stopPropagation();
    setProjectWorkspace(workspace);
    setNewProjectName('');
    setNewProjectColor('#4A9CC7');
    setCreateProjectOpen(true);
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim() || !projectWorkspace) return;
    setCreatingProject(true);
    try {
      await api.post('/projects', {
        name: newProjectName.trim(),
        workspace_id: projectWorkspace.workspace_id,
        color: newProjectColor,
        icon: newProjectIcon
      });
      toast.success('Project created!');
      setCreateProjectOpen(false);
      setWorkspaceProjects(prev => {
        const updated = { ...prev };
        delete updated[projectWorkspace.workspace_id];
        return updated;
      });
      setExpandedWorkspaces(prev => ({ ...prev, [projectWorkspace.workspace_id]: true }));
      setTimeout(() => fetchWorkspaceProjects(projectWorkspace.workspace_id), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  };

  const openProjectSettings = (project, workspace, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setEditingProject({ ...project, workspace_id: workspace.workspace_id });
    setEditProjectName(project.name);
    setEditProjectColor(project.color || '#4A9CC7');
    setEditProjectIcon(project.icon || 'folder');
    setProjectSettingsOpen(true);
  };

  const handleSaveProject = async () => {
    if (!editingProject || !editProjectName.trim()) return;
    setSavingProject(true);
    try {
      await api.patch(`/projects/${editingProject.project_id}`, {
        name: editProjectName.trim(),
        color: editProjectColor,
        icon: editProjectIcon
      });
      toast.success('Project updated!');
      setProjectSettingsOpen(false);
      setWorkspaceProjects(prev => {
        const updated = { ...prev };
        delete updated[editingProject.workspace_id];
        return updated;
      });
      fetchWorkspaceProjects(editingProject.workspace_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    } finally {
      setSavingProject(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!editingProject) return;
    if (!window.confirm(`Delete "${editingProject.name}"? All tasks in this project will be unassigned.`)) return;
    try {
      await api.delete(`/projects/${editingProject.project_id}`);
      toast.success('Project deleted');
      setProjectSettingsOpen(false);
      setWorkspaceProjects(prev => {
        const updated = { ...prev };
        delete updated[editingProject.workspace_id];
        return updated;
      });
      fetchWorkspaceProjects(editingProject.workspace_id);
      if (activeProjectId === editingProject.project_id) {
        navigate('/tasks');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const handleDuplicateProject = async () => {
    if (!editingProject) return;
    try {
      const response = await api.post(`/projects/${editingProject.project_id}/duplicate`);
      toast.success(`Project duplicated: ${response.data.name}`);
      setProjectSettingsOpen(false);
      setWorkspaceProjects(prev => {
        const updated = { ...prev };
        delete updated[editingProject.workspace_id];
        return updated;
      });
      fetchWorkspaceProjects(editingProject.workspace_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate project');
    }
  };

  // Workspace settings handlers
  const openSettingsDialog = async (workspace, e) => {
    e?.stopPropagation();
    setSettingsWorkspace(workspace);
    setSettingsDialogOpen(true);
    setLoadingMembers(true);
    try {
      const response = await api.get(`/workspaces/${workspace.workspace_id}`);
      setWorkspaceMembers(response.data.members || []);
    } catch (error) {
      toast.error('Failed to load workspace members');
      setWorkspaceMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!settingsWorkspace || !window.confirm('Remove this member from the workspace?')) return;
    try {
      await api.delete(`/workspaces/${settingsWorkspace.workspace_id}/members/${memberId}`);
      setWorkspaceMembers(prev => prev.filter(m => m.user_id !== memberId));
      toast.success('Member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleChangeMemberRole = async (memberId, newRole) => {
    if (!settingsWorkspace) return;
    try {
      await api.put(`/workspaces/${settingsWorkspace.workspace_id}/members/${memberId}/role`, { role: newRole });
      setWorkspaceMembers(prev => prev.map(m => m.user_id === memberId ? { ...m, role: newRole } : m));
      toast.success('Role updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDuplicateWorkspace = async () => {
    if (!settingsWorkspace) return;
    try {
      const response = await api.post(`/workspaces/${settingsWorkspace.workspace_id}/duplicate`);
      toast.success(`Workspace duplicated: ${response.data.name}`);
      setSettingsDialogOpen(false);
      refreshWorkspaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate workspace');
    }
  };

  const handleDeleteWorkspace = async (workspaceId, workspaceName) => {
    if (!window.confirm(`Are you sure you want to delete "${workspaceName}"? This will delete all projects and tasks.`)) return;
    try {
      await api.delete(`/workspaces/${workspaceId}`);
      toast.success('Workspace deleted');
      refreshWorkspaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete workspace');
    }
  };

  // Launchpad handlers
  const handleCreateLaunchpadFolder = async (data) => {
    setLaunchpadLoading(true);
    try {
      await launchpadApi.createFolder(data);
      toast.success('Folder created!');
      setCreateLaunchpadFolderOpen(false);
      // Trigger refresh in LaunchpadItem (both desktop and mobile)
      if (launchpadRef.current?.refreshFolders) {
        launchpadRef.current.refreshFolders();
      }
      if (mobileLaunchpadRef.current?.refreshFolders) {
        mobileLaunchpadRef.current.refreshFolders();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    } finally {
      setLaunchpadLoading(false);
    }
  };

  const handleEditLaunchpadFolder = async (data) => {
    if (!selectedLaunchpadFolder) return;
    setLaunchpadLoading(true);
    try {
      await launchpadApi.updateFolder(selectedLaunchpadFolder.launchpad_folder_id, data);
      toast.success('Folder updated!');
      setEditLaunchpadFolderOpen(false);
      setSelectedLaunchpadFolder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update folder');
    } finally {
      setLaunchpadLoading(false);
    }
  };

  const handleDeleteLaunchpadFolder = async () => {
    if (!selectedLaunchpadFolder) return;
    setLaunchpadLoading(true);
    try {
      await launchpadApi.deleteFolder(selectedLaunchpadFolder.launchpad_folder_id);
      toast.success('Folder deleted');
      setConfirmDeleteLaunchpadOpen(false);
      setEditLaunchpadFolderOpen(false);
      setSelectedLaunchpadFolder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete folder');
    } finally {
      setLaunchpadLoading(false);
    }
  };

  const handleShareLaunchpadFolder = async (shareData) => {
    if (!selectedLaunchpadFolder) return;
    setLaunchpadLoading(true);
    try {
      await launchpadApi.shareFolder(selectedLaunchpadFolder.launchpad_folder_id, shareData);
      toast.success('Folder shared!');
      // Refresh the share dialog
      setShareLaunchpadFolderOpen(false);
      setTimeout(() => setShareLaunchpadFolderOpen(true), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share folder');
    } finally {
      setLaunchpadLoading(false);
    }
  };

  const handleRemoveLaunchpadShare = async (share) => {
    if (!selectedLaunchpadFolder) return;
    setLaunchpadLoading(true);
    try {
      await launchpadApi.unshareFolder(selectedLaunchpadFolder.launchpad_folder_id, {
        user_ids: share.entity_type === 'user' ? [share.entity_id] : [],
        group_ids: share.entity_type === 'group' ? [share.entity_id] : [],
        permission: share.permission
      });
      toast.success('Access removed');
      // Refresh the share dialog
      setShareLaunchpadFolderOpen(false);
      setTimeout(() => setShareLaunchpadFolderOpen(true), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove access');
    } finally {
      setLaunchpadLoading(false);
    }
  };

  const handleAddLaunchpadLink = async (data) => {
    if (!selectedLaunchpadFolder) return;
    setLaunchpadLoading(true);
    try {
      await launchpadApi.createLink(selectedLaunchpadFolder.launchpad_folder_id, data);
      toast.success('Link added!');
      // Refresh the folder links immediately (both desktop and mobile)
      if (launchpadRef.current?.refreshFolderLinks) {
        launchpadRef.current.refreshFolderLinks(selectedLaunchpadFolder.launchpad_folder_id);
      }
      if (mobileLaunchpadRef.current?.refreshFolderLinks) {
        mobileLaunchpadRef.current.refreshFolderLinks(selectedLaunchpadFolder.launchpad_folder_id);
      }
      setLaunchpadLinkOpen(false);
      setSelectedLaunchpadFolder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add link');
    } finally {
      setLaunchpadLoading(false);
    }
  };

  const handleEditLaunchpadLink = async (data) => {
    if (!selectedLaunchpadLink) return;
    const folderId = selectedLaunchpadFolder?.launchpad_folder_id;
    setLaunchpadLoading(true);
    try {
      await launchpadApi.updateLink(selectedLaunchpadLink.link_id, data);
      toast.success('Link updated!');
      // Refresh the folder links immediately (both desktop and mobile)
      if (launchpadRef.current?.refreshFolderLinks && folderId) {
        launchpadRef.current.refreshFolderLinks(folderId);
      }
      if (mobileLaunchpadRef.current?.refreshFolderLinks && folderId) {
        mobileLaunchpadRef.current.refreshFolderLinks(folderId);
      }
      setLaunchpadLinkOpen(false);
      setSelectedLaunchpadLink(null);
      setSelectedLaunchpadFolder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update link');
    } finally {
      setLaunchpadLoading(false);
    }
  };

  const handleDeleteLaunchpadLink = async () => {
    if (!selectedLaunchpadLink) return;
    const folderId = selectedLaunchpadFolder?.launchpad_folder_id;
    setLaunchpadLoading(true);
    try {
      await launchpadApi.deleteLink(selectedLaunchpadLink.link_id);
      toast.success('Link deleted');
      // Refresh the folder links immediately (both desktop and mobile)
      if (launchpadRef.current?.refreshFolderLinks && folderId) {
        launchpadRef.current.refreshFolderLinks(folderId);
      }
      if (mobileLaunchpadRef.current?.refreshFolderLinks && folderId) {
        mobileLaunchpadRef.current.refreshFolderLinks(folderId);
      }
      setConfirmDeleteLaunchpadOpen(false);
      setSelectedLaunchpadLink(null);
      setSelectedLaunchpadFolder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete link');
    } finally {
      setLaunchpadLoading(false);
    }
  };

  // Navigation items
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/tasks', icon: CheckSquare, label: 'Tasks' },
    { path: '/calendar', icon: Calendar, label: 'Calendar' },
    { path: '/focus', icon: Timer, label: 'Focus' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleTaskCreated = () => {
    // Dispatch event for Tasks/Dashboard to refetch without full page reload
    window.dispatchEvent(new CustomEvent('task-created'));
  };

  return (
    <div className="h-screen flex bg-background">
      {/* Dialogs */}
      <CreateFolderDialog
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        folderName={newFolderName}
        setFolderName={setNewFolderName}
        folderColor={newFolderColor}
        setFolderColor={setNewFolderColor}
        onSubmit={handleCreateFolder}
        loading={creatingFolder}
      />

      <EditFolderDialog
        open={editFolderOpen}
        onOpenChange={setEditFolderOpen}
        folderName={editFolderName}
        setFolderName={setEditFolderName}
        folderColor={editFolderColor}
        setFolderColor={setEditFolderColor}
        onSave={handleSaveFolder}
        onDelete={handleDeleteFolder}
        loading={savingFolder}
      />

      <MoveToFolderDialog
        open={moveToFolderOpen}
        onOpenChange={setMoveToFolderOpen}
        workspaceName={movingWorkspace?.name}
        folders={folders}
        selectedFolderId={selectedFolderId}
        setSelectedFolderId={setSelectedFolderId}
        onMove={handleMoveToFolder}
        loading={movingToFolder}
      />

      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        workspaceName={inviteWorkspace?.name}
        workspaceId={inviteWorkspace?.workspace_id}
        email={inviteEmail}
        setEmail={setInviteEmail}
        role={inviteRole}
        setRole={setInviteRole}
        onInvite={(result) => {
          if (result?.invited !== undefined) {
            // Bulk invite result
            toast.success(`Invited ${result.invited} user(s) to ${inviteWorkspace?.name}`);
            setInviteDialogOpen(false);
            setInviteEmail('');
            setInviteRole('member');
            setInviteWorkspace(null);
            refreshWorkspaces();
          } else {
            // Single email invite
            handleInviteUser();
          }
        }}
        loading={inviteLoading}
        isAdmin={isAdmin}
      />

      <CreateProjectDialog
        open={createProjectOpen}
        onOpenChange={setCreateProjectOpen}
        workspaceName={projectWorkspace?.name}
        projectName={newProjectName}
        setProjectName={setNewProjectName}
        projectColor={newProjectColor}
        setProjectColor={setNewProjectColor}
        projectIcon={newProjectIcon}
        setProjectIcon={setNewProjectIcon}
        onSubmit={handleCreateProject}
        loading={creatingProject}
      />

      <ProjectSettingsDialog
        open={projectSettingsOpen}
        onOpenChange={setProjectSettingsOpen}
        projectId={editingProject?.project_id}
        projectName={editProjectName}
        setProjectName={setEditProjectName}
        projectColor={editProjectColor}
        setProjectColor={setEditProjectColor}
        projectIcon={editProjectIcon}
        setProjectIcon={setEditProjectIcon}
        onSave={handleSaveProject}
        onDelete={handleDeleteProject}
        onDuplicate={handleDuplicateProject}
        onMoveComplete={() => {
          // Refresh workspaces after project move
          fetchWorkspaces();
          if (editingProject?.workspace_id) {
            fetchWorkspaceProjects(editingProject.workspace_id);
          }
        }}
        loading={savingProject}
      />

      <WorkspaceSettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
        workspace={settingsWorkspace}
        members={workspaceMembers}
        loadingMembers={loadingMembers}
        onInvite={() => {
          setInviteWorkspace(settingsWorkspace);
          setSettingsDialogOpen(false);
          setInviteDialogOpen(true);
        }}
        onOpenChat={() => {
          navigate(`/chat?workspace=${settingsWorkspace?.workspace_id}`);
          setSettingsDialogOpen(false);
        }}
        onDuplicate={handleDuplicateWorkspace}
        onMoveToFolder={() => {
          setSettingsDialogOpen(false);
          openMoveToFolderDialog(settingsWorkspace);
        }}
        onChangeMemberRole={handleChangeMemberRole}
        onRemoveMember={handleRemoveMember}
        onDeleteWorkspace={() => {
          handleDeleteWorkspace(settingsWorkspace.workspace_id, settingsWorkspace.name);
          setSettingsDialogOpen(false);
        }}
      />

      {/* Launchpad Dialogs */}
      <CreateLaunchpadFolderDialog
        open={createLaunchpadFolderOpen}
        onOpenChange={setCreateLaunchpadFolderOpen}
        onSubmit={handleCreateLaunchpadFolder}
        loading={launchpadLoading}
      />

      <EditLaunchpadFolderDialog
        open={editLaunchpadFolderOpen}
        onOpenChange={setEditLaunchpadFolderOpen}
        folder={selectedLaunchpadFolder}
        onSave={handleEditLaunchpadFolder}
        onDelete={() => {
          setEditLaunchpadFolderOpen(false);
          setConfirmDeleteLaunchpadOpen(true);
        }}
        loading={launchpadLoading}
      />

      <ShareLaunchpadFolderDialog
        open={shareLaunchpadFolderOpen}
        onOpenChange={setShareLaunchpadFolderOpen}
        folder={selectedLaunchpadFolder}
        onShare={handleShareLaunchpadFolder}
        onRemoveShare={handleRemoveLaunchpadShare}
        loading={launchpadLoading}
      />

      <LaunchpadLinkDialog
        open={launchpadLinkOpen}
        onOpenChange={(open) => {
          setLaunchpadLinkOpen(open);
          if (!open) {
            setSelectedLaunchpadLink(null);
          }
        }}
        folder={selectedLaunchpadFolder}
        link={selectedLaunchpadLink}
        onSubmit={selectedLaunchpadLink ? handleEditLaunchpadLink : handleAddLaunchpadLink}
        loading={launchpadLoading}
      />

      <ConfirmDeleteLaunchpadDialog
        open={confirmDeleteLaunchpadOpen}
        onOpenChange={setConfirmDeleteLaunchpadOpen}
        itemType={selectedLaunchpadLink ? 'link' : 'folder'}
        itemName={selectedLaunchpadLink?.title || selectedLaunchpadFolder?.name}
        onConfirm={selectedLaunchpadLink ? handleDeleteLaunchpadLink : handleDeleteLaunchpadFolder}
        loading={launchpadLoading}
      />

      {/* Voice Input Modal */}
      <VoiceInput 
        open={voiceOpen} 
        onOpenChange={setVoiceOpen}
        onTaskCreated={handleTaskCreated}
      />

      {/* Unified Task Modal */}
      <UnifiedTaskModal
        open={taskModalOpen}
        onOpenChange={setTaskModalOpen}
        onTaskCreated={handleTaskCreated}
      />

      {/* Sidebar - Desktop */}
      <aside 
        ref={sidebarRef}
        className={`hidden md:flex flex-col bg-card border-r border-border transition-[width] ${isResizing ? 'duration-0' : 'duration-300'} relative`}
        style={{ width: sidebarCollapsed ? 64 : sidebarWidth }}
      >
        {/* Resize handle */}
        {!sidebarCollapsed && (
          <div
            className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize z-20 group hover:bg-[#4A9CC7]/30 active:bg-[#4A9CC7]/50 transition-colors"
            onMouseDown={startResize}
            onDoubleClick={resetSidebarWidth}
            data-testid="sidebar-resize-handle"
          >
            <div className="absolute top-1/2 -translate-y-1/2 right-0 w-1 h-8 rounded-full bg-muted-foreground/20 group-hover:bg-[#4A9CC7] transition-colors" />
          </div>
        )}
        {/* Logo & Collapse */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-border">
          {!sidebarCollapsed ? (
            <>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4A9CC7] to-[#2d7a9c] flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-lg text-foreground leading-tight">CORE</span>
                  <span className="text-[9px] font-semibold text-muted-foreground leading-tight tracking-wider">Create • Organize • Review • Execute</span>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => setSidebarCollapsed(true)}
                data-testid="collapse-sidebar-btn"
              >
                <Menu className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 mx-auto"
              onClick={() => setSidebarCollapsed(false)}
              data-testid="expand-sidebar-btn"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4A9CC7] to-[#2d7a9c] flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
            </Button>
          )}
        </div>

        {/* Add Task Button */}
        <div className="p-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => setTaskModalOpen(true)}
              className={`flex-1 bg-[#4A9CC7] hover:bg-[#3d8ab5] ${sidebarCollapsed ? 'px-2' : ''}`}
              data-testid="add-task-btn"
            >
              <Plus className="w-4 h-4" />
              {!sidebarCollapsed && <span className="ml-2">Add Task</span>}
            </Button>
            {!sidebarCollapsed && <NotificationBell />}
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Navigation */}
          <nav className="p-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                  ${isActive 
                    ? 'bg-[#4A9CC7]/10 text-[#4A9CC7]' 
                    : 'text-muted-foreground hover:bg-muted'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                {!sidebarCollapsed && (
                  <span className="text-xs font-semibold uppercase tracking-wider">{item.label}</span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Divider */}
          <div className="border-t border-border mx-3" />

          {/* Workspaces & Folders Section */}
          {!sidebarCollapsed && (
            <WorkspacesList
              folders={folders}
              unfiledWorkspaces={unfiledWorkspaces}
              workspaces={workspaces}
              expandedFolders={expandedFolders}
              expandedWorkspaces={expandedWorkspaces}
              workspaceProjects={workspaceProjects}
              loadingProjects={loadingProjects}
              currentWorkspace={currentWorkspace}
              activeProjectId={activeProjectId}
              onToggleFolder={toggleFolder}
              onToggleWorkspace={toggleWorkspace}
              onSwitchWorkspace={switchWorkspace}
              onCreateFolderClick={() => setCreateFolderOpen(true)}
              onEditFolder={openEditFolderDialog}
              onCreateProject={openCreateProjectDialog}
              onMoveToFolder={openMoveToFolderDialog}
              onInvite={openInviteDialog}
              onOpenSettings={openSettingsDialog}
              onOpenProjectSettings={openProjectSettings}
              workspaceUnread={workspaceUnread}
              projectUnread={projectUnread}
            />
          )}

          {/* Collapsed workspace indicator */}
          {sidebarCollapsed && (
            <div className="p-2">
              <Button
                variant="ghost"
                size="icon"
                className="w-full h-10"
                onClick={() => navigate('/workspaces')}
                title="Workspaces"
              >
                <FolderKanban className="w-5 h-5 text-muted-foreground" />
              </Button>
            </div>
          )}

          {/* Launchpad Section */}
          {!sidebarCollapsed && (
            <LaunchpadItem
              ref={launchpadRef}
              onCreateFolder={() => setCreateLaunchpadFolderOpen(true)}
              onEditFolder={(folder) => {
                setSelectedLaunchpadFolder(folder);
                setEditLaunchpadFolderOpen(true);
              }}
              onDeleteFolder={(folder) => {
                setSelectedLaunchpadFolder(folder);
                setSelectedLaunchpadLink(null);
                setConfirmDeleteLaunchpadOpen(true);
              }}
              onShareFolder={(folder) => {
                setSelectedLaunchpadFolder(folder);
                setShareLaunchpadFolderOpen(true);
              }}
              onAddLink={(folder) => {
                setSelectedLaunchpadFolder(folder);
                setSelectedLaunchpadLink(null);
                setLaunchpadLinkOpen(true);
              }}
              onEditLink={(link, folder) => {
                setSelectedLaunchpadFolder(folder);
                setSelectedLaunchpadLink(link);
                setLaunchpadLinkOpen(true);
              }}
              onDeleteLink={(link, folder) => {
                setSelectedLaunchpadFolder(folder);
                setSelectedLaunchpadLink(link);
                setConfirmDeleteLaunchpadOpen(true);
              }}
            />
          )}

          {/* Collapsed Launchpad indicator */}
          {sidebarCollapsed && (
            <div className="p-2">
              <Button
                variant="ghost"
                size="icon"
                className="w-full h-10"
                onClick={() => setCreateLaunchpadFolderOpen(true)}
                title="Launchpad"
              >
                <Rocket className="w-5 h-5 text-muted-foreground" />
              </Button>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-border mx-3" />

          {/* Chat & Other Links */}
          <nav className="p-3 space-y-1">
            {/* Chat with unread indicator */}
            <NavLink
              to="/chat"
              className={({ isActive }) => `
                flex items-center gap-2 px-3 py-2 rounded-lg transition-colors relative
                ${isActive 
                  ? 'bg-[#4A9CC7]/10 text-[#4A9CC7]' 
                  : 'text-muted-foreground hover:bg-muted'
                }
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
              data-testid="nav-chat"
            >
              <div className="relative">
                <MessageSquare className="w-4 h-4 flex-shrink-0" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white text-[10px] rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              {!sidebarCollapsed && <span className="text-xs font-semibold uppercase tracking-wider">Chat</span>}
            </NavLink>

            <NavLink
              to="/inbox"
              className={({ isActive }) => `
                flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                ${isActive 
                  ? 'bg-[#4A9CC7]/10 text-[#4A9CC7]' 
                  : 'text-muted-foreground hover:bg-muted'
                }
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
              data-testid="nav-inbox"
            >
              <Inbox className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && <span className="text-xs font-semibold uppercase tracking-wider">Inbox</span>}
            </NavLink>

            {/* Templates */}
            <NavLink
              to="/templates"
              className={({ isActive }) => `
                flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                ${isActive 
                  ? 'bg-[#4A9CC7]/10 text-[#4A9CC7]' 
                  : 'text-muted-foreground hover:bg-muted'
                }
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
              data-testid="nav-templates"
            >
              <FileText className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && <span className="text-xs font-semibold uppercase tracking-wider">Templates</span>}
            </NavLink>

            {/* Forms */}
            <NavLink
              to="/forms"
              className={({ isActive }) => `
                flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                ${isActive 
                  ? 'bg-[#4A9CC7]/10 text-[#4A9CC7]' 
                  : 'text-muted-foreground hover:bg-muted'
                }
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
              data-testid="nav-forms"
            >
              <ClipboardList className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && <span className="text-xs font-semibold uppercase tracking-wider">Forms</span>}
            </NavLink>

            {/* Voice Input Button */}
            <button
              onClick={() => setVoiceOpen(true)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors w-full text-muted-foreground hover:bg-muted ${sidebarCollapsed ? 'justify-center' : ''}`}
              data-testid="voice-input-btn"
            >
              <Mic className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && <span className="text-xs font-semibold uppercase tracking-wider">Voice Input</span>}
            </button>
          </nav>

          {/* Divider */}
          <div className="border-t border-border mx-3" />

          {/* Settings & Admin */}
          <nav className="p-3 space-y-1">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors w-full text-muted-foreground hover:bg-muted ${sidebarCollapsed ? 'justify-center' : ''}`}
              data-testid="theme-toggle-btn"
            >
              {isDark ? <Sun className="w-4 h-4 flex-shrink-0" /> : <Moon className="w-4 h-4 flex-shrink-0" />}
              {!sidebarCollapsed && <span className="text-xs font-semibold uppercase tracking-wider">{isDark ? 'Light Mode' : 'Dark Mode'}</span>}
            </button>

            <NavLink
              to="/settings"
              className={({ isActive }) => `
                flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                ${isActive 
                  ? 'bg-[#4A9CC7]/10 text-[#4A9CC7]' 
                  : 'text-muted-foreground hover:bg-muted'
                }
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
              data-testid="nav-settings"
            >
              <Settings className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && <span className="text-xs font-semibold uppercase tracking-wider">Settings</span>}
            </NavLink>

            {isAdmin && (
              <NavLink
                to="/admin"
                className={({ isActive }) => `
                  flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                  ${isActive 
                    ? 'bg-[#4A9CC7]/10 text-[#4A9CC7]' 
                    : 'text-muted-foreground hover:bg-muted'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                data-testid="nav-admin"
              >
                <Shield className="w-4 h-4 flex-shrink-0" />
                {!sidebarCollapsed && <span className="text-xs font-semibold uppercase tracking-wider">Admin</span>}
              </NavLink>
            )}
          </nav>
        </div>

        {/* User Profile Section */}
        <div className="border-t border-border p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-3 w-full p-2 rounded-lg hover:bg-muted transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-[#4A9CC7]/10 text-[#4A9CC7] text-sm">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                {!sidebarCollapsed && (
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 dark:bg-slate-800 dark:border-slate-700">
              <DropdownMenuLabel className="dark:text-gray-100">My Account</DropdownMenuLabel>
              <DropdownMenuSeparator className="dark:bg-slate-700" />
              <DropdownMenuItem onClick={() => navigate('/settings')} className="dark:text-gray-200 dark:focus:bg-slate-700">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/workspaces')} className="dark:text-gray-200 dark:focus:bg-slate-700">
                <FolderKanban className="w-4 h-4 mr-2" />
                Workspaces
              </DropdownMenuItem>
              <DropdownMenuSeparator className="dark:bg-slate-700" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400 dark:focus:bg-slate-700">
                <LogOut className="w-4 h-4 mr-2" />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header & Menu */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4A9CC7] to-[#2d7a9c] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-foreground">CORE</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTaskModalOpen(true)}
            >
              <Plus className="w-5 h-5" />
            </Button>
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-card border-b border-border shadow-lg max-h-[70vh] overflow-y-auto z-50">
            <nav className="p-4 space-y-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-[#4A9CC7]/10 text-[#4A9CC7] font-medium' 
                      : 'text-muted-foreground hover:bg-muted'
                    }
                  `}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
              
              <div className="border-t border-border my-2 pt-2">
                <NavLink
                  to="/chat"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-[#4A9CC7]/10 text-[#4A9CC7] font-medium' 
                      : 'text-muted-foreground hover:bg-muted'
                    }
                  `}
                >
                  <MessageSquare className="w-5 h-5" />
                  <span>Chat</span>
                  {unreadCount > 0 && (
                    <span className="ml-auto w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </NavLink>

                <NavLink
                  to="/inbox"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-[#4A9CC7]/10 text-[#4A9CC7] font-medium' 
                      : 'text-muted-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Inbox className="w-5 h-5" />
                  <span>Inbox</span>
                </NavLink>

                <NavLink
                  to="/templates"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-[#4A9CC7]/10 text-[#4A9CC7] font-medium' 
                      : 'text-muted-foreground hover:bg-muted'
                    }
                  `}
                >
                  <FileText className="w-5 h-5" />
                  <span>Templates</span>
                </NavLink>

                <NavLink
                  to="/forms"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-[#4A9CC7]/10 text-[#4A9CC7] font-medium' 
                      : 'text-muted-foreground hover:bg-muted'
                    }
                  `}
                >
                  <ClipboardList className="w-5 h-5" />
                  <span>Forms</span>
                </NavLink>
                
                <NavLink
                  to="/workspaces"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-[#4A9CC7]/10 text-[#4A9CC7] font-medium' 
                      : 'text-muted-foreground hover:bg-muted'
                    }
                  `}
                >
                  <FolderKanban className="w-5 h-5" />
                  <span>Workspaces</span>
                </NavLink>

                <button
                  onClick={() => { setVoiceOpen(true); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors w-full text-muted-foreground hover:bg-muted"
                >
                  <Mic className="w-5 h-5" />
                  <span>Voice Input</span>
                </button>
              </div>

              {/* Mobile Launchpad Section */}
              <MobileLaunchpad
                ref={mobileLaunchpadRef}
                onCreateFolder={() => setCreateLaunchpadFolderOpen(true)}
                onEditFolder={(folder) => {
                  setSelectedLaunchpadFolder(folder);
                  setEditLaunchpadFolderOpen(true);
                }}
                onDeleteFolder={(folder) => {
                  setSelectedLaunchpadFolder(folder);
                  setSelectedLaunchpadLink(null);
                  setConfirmDeleteLaunchpadOpen(true);
                }}
                onShareFolder={(folder) => {
                  setSelectedLaunchpadFolder(folder);
                  setShareLaunchpadFolderOpen(true);
                }}
                onAddLink={(folder) => {
                  setSelectedLaunchpadFolder(folder);
                  setSelectedLaunchpadLink(null);
                  setLaunchpadLinkOpen(true);
                }}
                onEditLink={(link, folder) => {
                  setSelectedLaunchpadFolder(folder);
                  setSelectedLaunchpadLink(link);
                  setLaunchpadLinkOpen(true);
                }}
                onDeleteLink={(link, folder) => {
                  setSelectedLaunchpadFolder(folder);
                  setSelectedLaunchpadLink(link);
                  setConfirmDeleteLaunchpadOpen(true);
                }}
                onClose={() => setMobileMenuOpen(false)}
              />

              <div className="border-t border-border my-2 pt-2">
                <button
                  onClick={toggleTheme}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors w-full text-muted-foreground hover:bg-muted"
                >
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                </button>

                <NavLink
                  to="/settings"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-[#4A9CC7]/10 text-[#4A9CC7] font-medium' 
                      : 'text-muted-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Settings className="w-5 h-5" />
                  <span>Settings</span>
                </NavLink>

                {isAdmin && (
                  <NavLink
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) => `
                      flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                      ${isActive 
                        ? 'bg-[#4A9CC7]/10 text-[#4A9CC7] font-medium' 
                        : 'text-muted-foreground hover:bg-muted'
                      }
                    `}
                  >
                    <Shield className="w-5 h-5" />
                    <span>Admin</span>
                  </NavLink>
                )}

                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors w-full text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Log Out</span>
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto md:pt-0 pt-14">
        {children}
      </main>
    </div>
  );
};

export default AppLayout;
